import React, {useMemo} from 'react'
import {Trans} from 'react-i18next'

import {Box, StepIcon} from '@mui/material'
import {getProductLineColors, PGTypography} from '@progynyglobal/pg-ui-library'

import {ReactComponent as EligibilityCheck} from '@progyny/assets/images/Animiation Eligibility Check.svg'
import {ReactComponent as BeginJourneyImage} from '@progyny/assets/images/Illustration - Begin Journey.svg'
import {ReactComponent as IllustrationImage} from '@progyny/assets/images/Illustration - Friends Sharing.svg'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {FERTILITY} from '@progyny/utils/constants'

const steps = [
  {
    icon: IllustrationImage,
    height: 23,
    label: <Trans i18nKey='switch_journey_change_story' />,
    description: <Trans i18nKey='switch_journey_life_evolves' />,
  },
  {
    icon: BeginJourneyImage,
    height: 23,
    label: <Trans i18nKey='switch_journey_product_guide_choose_your_path' />,
    description: (
      <>
        <PGTypography variant='subtitle'>
          <Trans i18nKey='switch_journey_product_guide_you_can' />:{' '}
        </PGTypography>
        <ul style={{marginTop: 4, marginBottom: 0}}>
          <li>
            <PGTypography component={'span'} variant='title'>
              <Trans i18nKey='switch_journey_journey_comparison_table_end_current_journey' />
              →{' '}
            </PGTypography>
            <PGTypography variant='subtitle' component={'span'}>
              <Trans i18nKey='switch_journey_product_guide_your_onboarding_resets_and_any' />
            </PGTypography>
          </li>
          <li>
            <PGTypography component={'span'} variant='title'>
              <Trans i18nKey='switch_journey_product_guide_add_journey' />→{' '}
            </PGTypography>
            <PGTypography variant='subtitle' component={'span'}>
              <Trans i18nKey='switch_journey_product_guide_keep_pregnancy_postpartum_alongside_ferti' />
            </PGTypography>
          </li>
        </ul>
      </>
    ),
  },
  {
    icon: EligibilityCheck,
    height: 14,
    label: (
      <Trans i18nKey='switch_journey_product_guide_your_benefits_stay_with_you' />
    ),
    description: (
      <Trans i18nKey='switch_journey_product_guide_your_fertility_reimbursement_benefits_sta' />
    ),
  },
  {
    icon: IllustrationImage,
    height: 0,
    label: (
      <Trans i18nKey='switch_journey_product_guide_you_can_always_return' />
    ),
    description: (
      <Trans i18nKey='switch_journey_product_guide_if_you_change_your_mind' />
    ),
  },
]

const ProductGuide = () => {
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)

  // Filter steps: show "Your benefits stay with you" only for fertility product line
  const filteredSteps = useMemo(() => {
    if (productLine === FERTILITY) {
      return steps
    }
    // Exclude the step with "Your benefits stay with you" (identified by EligibilityCheck icon)
    return steps.filter((step) => step.icon !== EligibilityCheck)
  }, [productLine])

  return (
    <Box sx={{mt: 4, ml: 2}}>
      {filteredSteps.map((step, index) => {
        const IconComponent = step.icon
        const isLast = index === filteredSteps.length - 1

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              position: 'relative',
              pb: isLast ? 0 : 6,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                mr: 3,
                zIndex: 1,
              }}
            >
              <StepIcon
                icon={index + 1}
                sx={{
                  color: colors.primaryLight,
                  border: `0.5px solid ${colors.primary}`,
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fff',
                  '& .MuiStepIcon-text': {
                    fill: colors.primary,
                    fontWeight: 'bold',
                  },
                }}
              />

              {/* Vertical solid line (only if not last step) */}
              {!isLast && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 28,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    height: `${step.height}rem`,
                    borderLeft: `2px dotted ${colors.primaryLight}`,
                    zIndex: 0,
                  }}
                />
              )}
            </Box>

            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
              <IconComponent width={80} height={80} style={{marginBottom: 8}} />
              <PGTypography variant='title'>{step.label}</PGTypography>
              <PGTypography variant='subtitle'>{step.description}</PGTypography>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default ProductGuide
